import {
  GradeSemanas,
  HorizonteMapa,
  MapaMarca,
  MapaResult,
  MovimentoPayload,
  SimulacaoResult,
} from '@/domain/mapa/interfaces/mapa';
import { mapaRepository } from '@/domain/mapa/repositories/mapaRepository';
import { EnrichedOp, IMudancaSelecionada } from '@/domain/ops/interfaces/ops';
import { opsRepository } from '@/domain/ops/repositories/opsRepository';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { createStore } from '@/lib/zustand';

/** The window the timeline opens with: what comes next, before the whole season. */
export const HORIZONTE_PADRAO: HorizonteMapa = 'DOZE_SEMANAS';

/** The whole scheduled portfolio: the window the brand colours are read from. */
const HORIZONTE_TUDO: HorizonteMapa = 'TUDO';

interface MapaState {
  mapa: MapaResult | null;
  loading: boolean;
  failed: boolean;
  /** The week x cell grid of the "Por semana" subtab, with its own pair of states. */
  grade: GradeSemanas | null;
  gradeLoading: boolean;
  gradeFailed: boolean;
  /**
   * The same grid with NO filter, for the screens that speak of the whole factory. The
   * Mapa's filters live in this store, so reusing `grade` would let a brand left selected
   * on the Mapa shrink the capacity the Visão executiva reports.
   */
  gradeDaFabrica: GradeSemanas | null;
  gradeDaFabricaLoading: boolean;
  gradeDaFabricaFailed: boolean;
  /**
   * As marcas da carteira INTEIRA, cada uma com a cor que o Mapa pinta nela.
   *
   * A cor vem do SERVIDOR: a escolhida no cadastro, ou a provisória que a leitura sugere
   * para quem ainda não escolheu nenhuma (`resolverCoresDasMarcas`). Toda tela que fala de
   * marca lê daqui, e não do `corMapa` do cadastro, senão a marca sem cor gravada aparece
   * cinza numa tela e colorida no Mapa.
   */
  marcasDaFabrica: Array<MapaMarca>;
  marcasDaFabricaLoading: boolean;
  marcasDaFabricaFailed: boolean;
  marca: string;
  /** Client (legal name): brings every brand of the group. */
  cliente: string;
  celula: string;
  horizonte: HorizonteMapa;
  /**
   * Which reading the timeline draws: today's schedule or the proposal's. It is only for
   * looking, nothing is saved, and the subtab's selector is what flips it.
   */
  vendoProposta: boolean;
  /** The map as it WOULD be with the ticked changes; null while nobody asked for it. */
  mapaDaProposta: MapaResult | null;
  mapaDaPropostaLoading: boolean;
  mapaDaPropostaFailed: boolean;
  /**
   * The same reading for the "Por semana" subtab: the grid as it WOULD be. The two
   * subtabs are two readings of the same portfolio, so the selector has to answer on
   * both, and the grid is a different computation from the timeline's.
   */
  gradeDaProposta: GradeSemanas | null;
  gradeDaPropostaLoading: boolean;
  gradeDaPropostaFailed: boolean;
  /** The ticked changes the "Proposta" side draws, so a filter change can reload it. */
  mudancasDaProposta: Array<IMudancaSelecionada>;
  simulacao: SimulacaoResult | null;
  simulando: boolean;
  aplicando: boolean;
  simulacaoError: string | null;
  ficha: EnrichedOp | null;
  fichaLoading: boolean;
  /** API message of the last failed ficha load; null while nothing failed. */
  fichaError: string | null;
  loadMapa: () => Promise<void>;
  loadGrade: () => Promise<void>;
  /** Loads the grid of the whole factory. Never throws: the screen reads the failed flag. */
  loadGradeDaFabrica: () => Promise<void>;
  /** Loads the brands of the whole portfolio with their colours. Never throws. */
  loadMarcasDaFabrica: () => Promise<void>;
  reloadCarteira: () => Promise<void>;
  /** Brings the proposal reading along after the portfolio changed. No-op on "Atual". */
  reloadProposta: () => Promise<void>;
  setMarca: (marca: string) => Promise<void>;
  setCliente: (cliente: string) => Promise<void>;
  setCelula: (celula: string) => Promise<void>;
  setHorizonte: (horizonte: HorizonteMapa) => Promise<void>;
  /** Draws the proposal on BOTH subtabs, with the screen's own filters. Saves nothing. */
  verProposta: (mudancas: Array<IMudancaSelecionada>) => Promise<void>;
  /** Back to today's schedule, and the proposal's reading is dropped. */
  verAtual: () => void;
  simular: (payload: MovimentoPayload) => Promise<void>;
  aplicar: (payload: MovimentoPayload) => Promise<void>;
  clearSimulacao: () => void;
  openFicha: (uid: string) => Promise<void>;
  closeFicha: () => void;
}

// Filters fire one load each; only the latest response may paint the map.
let loadSequence = 0;
// The grid answers on its own request, so it carries its own sequence guard.
let gradeSequence = 0;
// The unfiltered grid is another request, with the same guard of its own.
let gradeDaFabricaSequence = 0;
// The brands of the whole portfolio answer on their own request, with their own guard.
let marcasDaFabricaSequence = 0;
// Closing the drawer (or opening another OP) while a ficha loads discards that load.
let fichaSequence = 0;
// Unticking a change (or going back to "Atual") while the proposal map loads drops it.
let propostaSequence = 0;

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** The filters both proposal readings travel with, so they match the "Atual" side. */
interface FiltrosDaProposta {
  marca: string;
  cliente: string;
  celula: string;
  horizonte?: HorizonteMapa;
}

/**
 * The timeline's proposal reading. It lands with its OWN pair of states (as `mapa` does
 * on the "Atual" side), so the grid failing never blanks the timeline, or the other way
 * around. A newer reading (or going back to "Atual") bumps the sequence and this one is
 * dropped on arrival.
 */
async function carregarMapaDaProposta(
  sequence: number,
  mudancas: Array<IMudancaSelecionada>,
  filtros: FiltrosDaProposta,
): Promise<void> {
  try {
    const mapaDaProposta = await mapaRepository.getMapaDaProposta(mudancas, filtros);
    if (sequence === propostaSequence) {
      mapaStore.setState({ mapaDaProposta });
    }
  } catch (error) {
    logger.error('[mapaStore] failed to load the proposal map', error);
    if (sequence === propostaSequence) {
      mapaStore.setState({ mapaDaProposta: null, mapaDaPropostaFailed: true });
    }
  } finally {
    if (sequence === propostaSequence) {
      mapaStore.setState({ mapaDaPropostaLoading: false });
    }
  }
}

/** The same reading for the "Por semana" subtab, with its own pair of states. */
async function carregarGradeDaProposta(
  sequence: number,
  mudancas: Array<IMudancaSelecionada>,
  filtros: Omit<FiltrosDaProposta, 'horizonte'>,
): Promise<void> {
  try {
    const gradeDaProposta = await mapaRepository.getGradeDaProposta(mudancas, filtros);
    if (sequence === propostaSequence) {
      mapaStore.setState({ gradeDaProposta });
    }
  } catch (error) {
    logger.error('[mapaStore] failed to load the proposal grade semanas', error);
    if (sequence === propostaSequence) {
      mapaStore.setState({ gradeDaProposta: null, gradeDaPropostaFailed: true });
    }
  } finally {
    if (sequence === propostaSequence) {
      mapaStore.setState({ gradeDaPropostaLoading: false });
    }
  }
}

export const mapaStore = createStore<MapaState>((set, get) => ({
  mapa: null,
  loading: false,
  failed: false,
  grade: null,
  gradeLoading: false,
  gradeFailed: false,
  gradeDaFabrica: null,
  gradeDaFabricaLoading: false,
  gradeDaFabricaFailed: false,
  marcasDaFabrica: [],
  marcasDaFabricaLoading: false,
  marcasDaFabricaFailed: false,
  marca: '',
  cliente: '',
  celula: '',
  horizonte: HORIZONTE_PADRAO,
  vendoProposta: false,
  mapaDaProposta: null,
  mapaDaPropostaLoading: false,
  mapaDaPropostaFailed: false,
  gradeDaProposta: null,
  gradeDaPropostaLoading: false,
  gradeDaPropostaFailed: false,
  mudancasDaProposta: [],
  simulacao: null,
  simulando: false,
  aplicando: false,
  simulacaoError: null,
  ficha: null,
  fichaLoading: false,
  fichaError: null,

  loadMapa: async () => {
    loadSequence += 1;
    const sequence = loadSequence;
    set({ loading: true });
    try {
      const { marca, cliente, celula, horizonte } = get();
      const mapa = await mapaRepository.getMapa({ marca, cliente, celula, horizonte });
      if (sequence === loadSequence) {
        set({ mapa, failed: false });
      }
    } catch (error) {
      logger.error('[mapaStore] failed to load mapa', error);
      if (sequence === loadSequence) {
        set({ failed: true });
      }
    } finally {
      if (sequence === loadSequence) {
        set({ loading: false });
      }
    }
  },

  loadGrade: async () => {
    gradeSequence += 1;
    const sequence = gradeSequence;
    set({ gradeLoading: true });
    try {
      const { marca, cliente, celula } = get();
      const grade = await mapaRepository.getGradeSemanas({ marca, cliente, celula });
      if (sequence === gradeSequence) {
        set({ grade, gradeFailed: false });
      }
    } catch (error) {
      logger.error('[mapaStore] failed to load grade semanas', error);
      if (sequence === gradeSequence) {
        set({ gradeFailed: true });
      }
    } finally {
      if (sequence === gradeSequence) {
        set({ gradeLoading: false });
      }
    }
  },

  loadGradeDaFabrica: async () => {
    gradeDaFabricaSequence += 1;
    const sequence = gradeDaFabricaSequence;
    set({ gradeDaFabricaLoading: true });
    try {
      const gradeDaFabrica = await mapaRepository.getGradeSemanas();
      if (sequence === gradeDaFabricaSequence) {
        set({ gradeDaFabrica, gradeDaFabricaFailed: false });
      }
    } catch (error) {
      logger.error('[mapaStore] failed to load grade semanas da fabrica', error);
      if (sequence === gradeDaFabricaSequence) {
        set({ gradeDaFabricaFailed: true });
      }
    } finally {
      if (sequence === gradeDaFabricaSequence) {
        set({ gradeDaFabricaLoading: false });
      }
    }
  },

  /**
   * The brands with their colours, read UNFILTERED and over the whole horizon: a brand
   * scheduled a year out still has to keep its colour, and the Mapa's own filters live in
   * this store. Only the legend is kept, not the timeline that came with it.
   */
  loadMarcasDaFabrica: async () => {
    marcasDaFabricaSequence += 1;
    const sequence = marcasDaFabricaSequence;
    set({ marcasDaFabricaLoading: true });
    try {
      const { marcas } = await mapaRepository.getMapa({ horizonte: HORIZONTE_TUDO });
      if (sequence === marcasDaFabricaSequence) {
        set({ marcasDaFabrica: marcas ?? [], marcasDaFabricaFailed: false });
      }
    } catch (error) {
      logger.error('[mapaStore] failed to load marcas da fabrica', error);
      if (sequence === marcasDaFabricaSequence) {
        set({ marcasDaFabricaFailed: true });
      }
    } finally {
      if (sequence === marcasDaFabricaSequence) {
        set({ marcasDaFabricaLoading: false });
      }
    }
  },

  /**
   * The two subtabs are two readings of the same portfolio, so whatever changes both
   * reloads both. The grid is skipped while it was never opened: its own first load
   * already carries the current filters.
   */
  reloadCarteira: async () => {
    const { grade, gradeFailed } = get();
    const recargas = [get().loadMapa(), get().reloadProposta()];
    if (grade !== null || gradeFailed) {
      recargas.push(get().loadGrade());
    }
    await Promise.all(recargas);
  },

  /**
   * The proposal reading is computed from the CURRENT portfolio and on the SAME filters,
   * so whatever changes either has to reach it too: leaving it behind would draw
   * "Proposta" squares from a portfolio (or a brand) that "Atual" no longer shows.
   */
  reloadProposta: async () => {
    const { vendoProposta, mudancasDaProposta } = get();
    if (!vendoProposta) {
      return;
    }
    await get().verProposta(mudancasDaProposta);
  },

  setMarca: async (marca) => {
    set({ marca });
    await get().reloadCarteira();
  },

  setCliente: async (cliente) => {
    set({ cliente });
    await get().reloadCarteira();
  },

  setCelula: async (celula) => {
    set({ celula });
    await get().reloadCarteira();
  },

  /** The horizon only widens the timeline window: the grid always covers everything. */
  setHorizonte: async (horizonte) => {
    set({ horizonte });
    await Promise.all([get().loadMapa(), get().reloadProposta()]);
  },

  /**
   * The proposal's bands and squares, read through the same engine and the same rulers
   * as today's: that is what makes the two sides of the selector comparable band by band
   * and square by square. BOTH subtabs are loaded at once, so switching tab never shows
   * one reading on one side and another on the other.
   */
  verProposta: async (mudancas) => {
    propostaSequence += 1;
    const sequence = propostaSequence;
    const { marca, cliente, celula, horizonte } = get();
    set({
      vendoProposta: true,
      mudancasDaProposta: mudancas,
      mapaDaPropostaLoading: true,
      mapaDaPropostaFailed: false,
      gradeDaPropostaLoading: true,
      gradeDaPropostaFailed: false,
    });
    await Promise.all([
      carregarMapaDaProposta(sequence, mudancas, { marca, cliente, celula, horizonte }),
      // The grid always covers the whole portfolio: it takes no horizon.
      carregarGradeDaProposta(sequence, mudancas, { marca, cliente, celula }),
    ]);
  },

  verAtual: () => {
    propostaSequence += 1;
    set({
      vendoProposta: false,
      mapaDaProposta: null,
      mapaDaPropostaLoading: false,
      mapaDaPropostaFailed: false,
      gradeDaProposta: null,
      gradeDaPropostaLoading: false,
      gradeDaPropostaFailed: false,
      mudancasDaProposta: [],
    });
  },

  simular: async (payload) => {
    set({ simulando: true, simulacao: null, simulacaoError: null });
    try {
      const simulacao = await mapaRepository.simular(payload);
      set({ simulacao });
    } catch (error) {
      logger.error('[mapaStore] failed to simulate movement', error);
      set({ simulacaoError: messageOf(error) });
    } finally {
      set({ simulando: false });
    }
  },

  /**
   * The API answers with the whole map, unfiltered and on the default horizon. That
   * answer is only the screen's map when the screen asks for exactly that; otherwise
   * the map is reloaded with what the screen has selected.
   *
   * The movement also changes the grid (the OP left one square and landed on another),
   * so it is reloaded too, respecting the same filters.
   */
  aplicar: async (payload) => {
    set({ aplicando: true });
    try {
      const mapa = await mapaRepository.aplicar(payload);
      const { marca, cliente, celula, horizonte } = get();
      const respostaServeATela = marca === '' && cliente === '' && celula === '' && horizonte === HORIZONTE_PADRAO;
      set({ simulacao: null, simulacaoError: null });
      if (respostaServeATela) {
        loadSequence += 1;
        set({ mapa, failed: false });
        await Promise.all([get().loadGrade(), get().reloadProposta()]);
      } else {
        await get().reloadCarteira();
      }
    } catch (error) {
      logger.error('[mapaStore] failed to apply movement', error);
      set({ simulacaoError: messageOf(error) });
    } finally {
      set({ aplicando: false });
    }
  },

  clearSimulacao: () => {
    set({ simulacao: null, simulacaoError: null });
  },

  openFicha: async (uid) => {
    fichaSequence += 1;
    const sequence = fichaSequence;
    set({ fichaLoading: true, ficha: null, fichaError: null });
    try {
      const ficha = await opsRepository.findByUid(uid);
      if (sequence === fichaSequence) {
        set({ ficha });
      }
    } catch (error) {
      logger.error('[mapaStore] failed to load OP ficha', error);
      if (sequence === fichaSequence) {
        set({ fichaError: messageOf(error) });
      }
    } finally {
      if (sequence === fichaSequence) {
        set({ fichaLoading: false });
      }
    }
  },

  closeFicha: () => {
    fichaSequence += 1;
    set({ ficha: null, fichaLoading: false, fichaError: null });
  },
}));
